"use client"

import { useState, useRef, useEffect } from "react"
import { Clock, IndianRupee, Search, Plus, User, CheckCircle, AlertCircle, SplitSquareHorizontal, Building2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { formatItemDisplayName } from "./inventoryDetailsUtils"
import { createCustomer, createVendor } from "../../../services/ledgerService"
import toast from "react-hot-toast"

export default function InventoryManagementDialogs({
  // Shared
  loading,
  setMessage,

  // Inventory for purchase select
  inventory,

  // Reference data
  categories,
  vendors,
  units,
  sizes,
  colors,
  isColorEnabled,

  // Selection
  selectedItem,
  setSelectedItem,

  // Purchase
  showPurchaseForm,
  setShowPurchaseForm,
  newPurchase,
  setNewPurchase,
  handleAddPurchase,

  // Issue
  showIssueForm,
  setShowIssueForm,
  newIssue,
  setNewIssue,
  issueLedger,
  setIssueLedger,
  issueLedgerData,
  setIssueLedgerData,
  customers,
  handleIssueStock,

  // Adjust
  showAdjustForm,
  setShowAdjustForm,
  newAdjustment,
  setNewAdjustment,
  handleAdjustStock,

  // Unhold
  showUnholdForm,
  setShowUnholdForm,
  unholdQuantity,
  setUnholdQuantity,
  handleUnholdStock,

  // Hold
  showHoldForm,
  setShowHoldForm,
  holdQuantity,
  setHoldQuantity,
  handleHoldStock,
}) {
  // Issue Stock local state
  const [partySearch, setPartySearch] = useState("")
  const [selectedParty, setSelectedParty] = useState(null) // { _id, name, phone } | null
  const [showPartyDrop, setShowPartyDrop] = useState(false)
  const [paymentMode, setPaymentMode] = useState("") // "yes" | "partial" | "no"
  const [issueTotal, setIssueTotal] = useState("")
  const [issuePaid, setIssuePaid] = useState("")
  const [issueDue, setIssueDue] = useState("")
  const [creatingParty, setCreatingParty] = useState(false)
  const partyRef = useRef(null)

  // Purchase vendor search state
  const [vendorSearch, setVendorSearch] = useState("")
  const [selectedVendor, setSelectedVendor] = useState(null)
  const [showVendorDrop, setShowVendorDrop] = useState(false)
  const [purchasePaymentMode, setPurchasePaymentMode] = useState("")
  const [purchaseTotal, setPurchaseTotal] = useState("")
  const [purchasePaid, setPurchasePaid] = useState("")
  const [purchaseDue, setPurchaseDue] = useState("")
  const [creatingVendor, setCreatingVendor] = useState(false)

  // Local error for purchase dialog (displayed inside the dialog, not behind it)
  const [purchaseDialogError, setPurchaseDialogError] = useState("")

  // Quantity + cost for purchase — brand is taken from the selected item itself
  const [purchaseQuantity, setPurchaseQuantity] = useState("")
  const [purchaseCostPerUnit, setPurchaseCostPerUnit] = useState("")
  const [purchaseTotalManuallyEdited, setPurchaseTotalManuallyEdited] = useState(false)
  const brandRowsTotal = (parseFloat(purchaseQuantity) || 0) * (parseFloat(purchaseCostPerUnit) || 0)

  // Auto-populate Total Amount from quantity * cost unless user manually overrode it
  useEffect(() => {
    if (!purchaseTotalManuallyEdited && brandRowsTotal > 0) {
      setPurchaseTotal(String(brandRowsTotal))
    }
  }, [brandRowsTotal, purchaseTotalManuallyEdited])

  // Reset issue form when dialog closes
  useEffect(() => {
    if (!showIssueForm) {
      setPartySearch("")
      setSelectedParty(null)
      setShowPartyDrop(false)
      setPaymentMode("")
      setIssueTotal("")
      setIssuePaid("")
      setIssueDue("")
    }
  }, [showIssueForm])

  // Reset purchase form when dialog closes
  useEffect(() => {
    if (!showPurchaseForm) {
      setVendorSearch("")
      setSelectedVendor(null)
      setShowVendorDrop(false)
      setPurchasePaymentMode("")
      setPurchaseTotal("")
      setPurchasePaid("")
      setPurchaseDue("")
      setPurchaseQuantity("")
      setPurchaseCostPerUnit("")
      setPurchaseDialogError("")
      setPurchaseTotalManuallyEdited(false)
    }
  }, [showPurchaseForm])

  const filteredVendors = vendorSearch.trim()
    ? (vendors || []).filter(v => v.name?.toLowerCase().includes(vendorSearch.toLowerCase()))
    : (vendors || []).slice(0, 10)

  const exactVendorMatch = (vendors || []).find(
    v => v.name?.toLowerCase() === vendorSearch.trim().toLowerCase()
  )

  const handleCreateAndSelectVendor = async () => {
    if (!vendorSearch.trim()) return
    try {
      setCreatingVendor(true)
      const res = await createVendor({ name: vendorSearch.trim() })
      const created = res.data || res
      setSelectedVendor({ _id: created._id, name: created.name })
      setVendorSearch(created.name)
      setShowVendorDrop(false)
    } catch (err) {
      // Prefer server-provided error details and show them inside the modal using setMessage.
      const resp = err?.response?.data
      if (resp?.existing && resp.existing.length > 0) {
        const first = resp.existing[0]
        setSelectedVendor({ _id: first._id, name: first.name })
        setVendorSearch(first.name)
        setShowVendorDrop(false)
        setMessage({ type: "error", content: resp.message || "Vendor already exists; selected existing vendor" })
      } else {
        // Show exact error message from server if present, otherwise a fallback
        const msg = resp?.message || err?.message || "Failed to add vendor"
        setMessage({ type: "error", content: `Failed to add vendor: ${msg}` })
        setSelectedVendor({ _id: null, name: vendorSearch.trim() })
        setShowVendorDrop(false)
      }
    } finally {
      setCreatingVendor(false)
    }
  }

  const handlePurchaseSubmit = () => {
    setPurchaseDialogError("")
    // If the user typed a vendor name but never selected or created one, block submission
    if (vendorSearch.trim() && !selectedVendor?._id) {
      setPurchaseDialogError("Please select a vendor from the list or click \"Create\" to add them as a new vendor.")
      return
    }
    if ((parseFloat(purchaseQuantity) || 0) <= 0) {
      setPurchaseDialogError("Please enter a quantity greater than 0")
      return
    }
    const validRows = [{ brand: selectedItem?.brand || "", quantity: purchaseQuantity, cost_per_unit: purchaseCostPerUnit }]
    const total = parseFloat(purchaseTotal) || brandRowsTotal
    let paid = 0
    if (purchasePaymentMode === "yes") paid = total
    else if (purchasePaymentMode === "partial") paid = parseFloat(purchasePaid) || 0
    handleAddPurchase({
      vendor: selectedVendor ? { id: selectedVendor._id, name: selectedVendor.name } : null,
      payment: purchasePaymentMode ? {
        total_amount: total,
        paid_amount: paid,
        due_date: purchaseDue || null,
        status: purchasePaymentMode,
      } : null,
      brandRows: validRows,
    })
  }

  const filteredParties = partySearch.trim()
    ? (customers || []).filter(c =>
        c.name?.toLowerCase().includes(partySearch.toLowerCase()) ||
        c.phone?.includes(partySearch)
      )
    : (customers || [])

  const exactMatch = (customers || []).find(
    c => c.name?.toLowerCase() === partySearch.trim().toLowerCase()
  )

  const handleCreateAndSelectParty = async () => {
    if (!partySearch.trim()) return
    try {
      setCreatingParty(true)
      const res = await createCustomer({ name: partySearch.trim() })
      const created = res.data || res
      setSelectedParty({ _id: created._id, name: created.name, phone: created.phone })
      setPartySearch(created.name)
      setShowPartyDrop(false)
    } catch {
      // fallback: just store name, let backend handle
    } finally {
      setCreatingParty(false)
    }
  }

  const handleIssueSubmit = async () => {
    // The party (real Customer record) is independent of whether ledger/payment
    // tracking is turned on — picking a party should always attach customer_id
    // to the issue, even if the staff never touches the payment toggle below it.
    let ledgerOverride = { enabled: false, data: {} }
    if (selectedParty) {
      ledgerOverride.data.customer_id = selectedParty._id
      ledgerOverride.data.customer_name = selectedParty.name
    }
    if (paymentMode && selectedParty) {
      const totalRaw = issueTotal.trim()
      const total = totalRaw ? parseFloat(totalRaw) : null
      // partial and unpaid modes require total_amount
      if ((paymentMode === "partial" || paymentMode === "no") && total === null) {
        setMessage({ type: "error", content: "Please enter total amount for ledger tracking" })
        return
      }
      if (total !== null) {
        let paid = 0
        if (paymentMode === "yes") paid = total
        else if (paymentMode === "partial") paid = parseFloat(issuePaid) || 0
        ledgerOverride = {
          enabled: true,
          data: {
            customer_id: selectedParty._id,
            customer_name: selectedParty.name,
            total_amount: String(total),
            paid_amount: String(paid),
            due_date: issueDue || null,
          }
        }
      }
    }
    handleIssueStock(ledgerOverride)
  }


  return (
    <>
      {/* Add Purchase Dialog */}
      <Dialog open={showPurchaseForm} onOpenChange={setShowPurchaseForm}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Add Purchase Record</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-4">
            {/* Item */}
            <div>
              <Label htmlFor="purchase-item">Item (Size _ Brand _ Name)</Label>
              <Select
                value={selectedItem?.inventory_id?.toString() || "-1"}
                onValueChange={(value) => {
                  const item = inventory.find((i) => i.inventory_id === Number.parseInt(value, 10))
                  setSelectedItem(item || null)
                }}
              >
                <SelectTrigger id="purchase-item">
                  <SelectValue placeholder="Select item" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="-1">Select item</SelectItem>
                  {inventory.map((item) => (
                    <SelectItem key={item.inventory_id} value={item.inventory_id.toString()}>
                      {formatItemDisplayName(item)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date */}
            <div>
              <Label htmlFor="purchase-date">Purchase Date</Label>
              <Input
                id="purchase-date"
                type="date"
                value={newPurchase.date}
                max={new Date().toISOString().split('T')[0]}
                onChange={(e) => setNewPurchase({ ...newPurchase, date: e.target.value })}
              />
            </div>

            {/* Vendor Search (B2B) */}
            <div className="border-t pt-4">
              <Label>Vendor (B2B)</Label>
              <p className="text-xs text-gray-400 mb-1.5">Select existing vendor or type to create new</p>
              <div className="relative">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search or enter vendor name..."
                    className="pl-9"
                    value={selectedVendor ? selectedVendor.name : vendorSearch}
                    onChange={(e) => {
                      setVendorSearch(e.target.value)
                      setSelectedVendor(null)
                      setShowVendorDrop(true)
                    }}
                    onFocus={() => setShowVendorDrop(true)}
                    onBlur={() => setTimeout(() => setShowVendorDrop(false), 150)}
                  />
                  {selectedVendor && (
                    <button
                      onClick={() => { setSelectedVendor(null); setVendorSearch(""); setPurchasePaymentMode("") }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs"
                    >✕</button>
                  )}
                </div>

                {showVendorDrop && !selectedVendor && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {filteredVendors.length > 0 && filteredVendors.map(v => (
                      <button
                        key={v._id}
                        className="w-full text-left px-4 py-2.5 hover:bg-gray-50 flex items-center gap-3 border-b border-gray-50 last:border-0"
                        onMouseDown={() => {
                          setSelectedVendor(v)
                          setVendorSearch(v.name)
                          setShowVendorDrop(false)
                        }}
                      >
                        <div className="bg-purple-100 rounded-full p-1.5">
                          <Building2 className="w-3 h-3 text-purple-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{v.name}</p>
                          {v.phone && <p className="text-xs text-gray-400">{v.phone}</p>}
                        </div>
                      </button>
                    ))}
                    {vendorSearch.trim() && !exactVendorMatch && (
                      <button
                        className="w-full text-left px-4 py-2.5 hover:bg-purple-50 flex items-center gap-3 text-purple-600 border-t border-gray-100"
                        onMouseDown={handleCreateAndSelectVendor}
                        disabled={creatingVendor}
                      >
                        <div className="bg-purple-600 rounded-full p-1.5">
                          <Plus className="w-3 h-3 text-white" />
                        </div>
                        <span className="text-sm font-medium">
                          {creatingVendor ? "Creating..." : `Create "${vendorSearch.trim()}" as new vendor`}
                        </span>
                      </button>
                    )}
                    {filteredVendors.length === 0 && !vendorSearch.trim() && (
                      <div className="px-4 py-3 text-sm text-gray-400 text-center">
                        No vendors yet. Type a name to create one.
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Payment Mode — only show if vendor selected */}
            {selectedVendor && (
              <div>
                <Label>Payment to Vendor</Label>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {[
                    { value: "yes", label: "Paid", icon: CheckCircle, color: "green" },
                    { value: "partial", label: "Partial", icon: SplitSquareHorizontal, color: "yellow" },
                    { value: "no", label: "Unpaid", icon: AlertCircle, color: "red" },
                  ].map(opt => {
                    const Icon = opt.icon
                    const active = purchasePaymentMode === opt.value
                    const colorMap = {
                      green: active ? "bg-green-600 text-white border-green-600" : "border-gray-200 text-gray-600 hover:border-green-400 hover:text-green-600",
                      yellow: active ? "bg-yellow-500 text-white border-yellow-500" : "border-gray-200 text-gray-600 hover:border-yellow-400 hover:text-yellow-600",
                      red: active ? "bg-red-600 text-white border-red-600" : "border-gray-200 text-gray-600 hover:border-red-400 hover:text-red-600",
                    }
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setPurchasePaymentMode(opt.value)}
                        className={`flex flex-col items-center gap-1 py-3 rounded-xl border-2 transition-all text-sm font-medium ${colorMap[opt.color]}`}
                      >
                        <Icon className="w-4 h-4" />
                        {opt.label}
                      </button>
                    )
                  })}
                </div>
              </div>
            )}

            {/* Amount fields */}
            {selectedVendor && (purchasePaymentMode === "yes" || purchasePaymentMode === "partial" || purchasePaymentMode === "no") && (
              <div className="space-y-3">
                <div>
                  <Label>Total Amount *</Label>
                  <div className="relative mt-1">
                    <IndianRupee className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                    <Input
                      type="number" min="0" className="pl-7" placeholder="0.00"
                      value={purchaseTotal}
                      onChange={(e) => { setPurchaseTotal(e.target.value); setPurchaseTotalManuallyEdited(true) }}
                      onFocus={(e) => e.target.select()}
                    />
                  </div>
                  {brandRowsTotal > 0 && parseFloat(purchaseTotal) !== brandRowsTotal && (
                    <p className="text-xs text-amber-600 mt-1">
                      Brand subtotal is ₹{brandRowsTotal.toLocaleString("en-IN")} — adjust Total Amount if needed
                    </p>
                  )}
                </div>
                {purchasePaymentMode === "partial" && (
                  <div>
                    <Label>Amount Paid Now</Label>
                    <div className="relative mt-1">
                      <IndianRupee className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                      <Input type="number" min="0" className="pl-7" placeholder="0.00" value={purchasePaid} onChange={(e) => setPurchasePaid(e.target.value)} onFocus={(e) => e.target.select()} />
                    </div>
                  </div>
                )}
                {(purchasePaymentMode === "partial" || purchasePaymentMode === "no") && (
                  <div>
                    <Label>Due Date</Label>
                    <Input type="date" className="mt-1" value={purchaseDue} onChange={(e) => setPurchaseDue(e.target.value)} />
                  </div>
                )}
              </div>
            )}

            {/* Quantity & cost — brand is taken from the selected item */}
            <div className="border-t pt-4 grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="purchase-quantity">Quantity *</Label>
                <Input
                  id="purchase-quantity"
                  type="number" min="0" step="any"
                  value={purchaseQuantity}
                  onChange={(e) => setPurchaseQuantity(e.target.value)}
                  onFocus={(e) => e.target.select()}
                  placeholder="0"
                />
              </div>
              <div>
                <Label htmlFor="purchase-cost">Cost per Unit</Label>
                <div className="relative">
                  <IndianRupee className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                  <Input
                    id="purchase-cost"
                    type="number" min="0" step="any" className="pl-7"
                    value={purchaseCostPerUnit}
                    onChange={(e) => setPurchaseCostPerUnit(e.target.value)}
                    onFocus={(e) => e.target.select()}
                    placeholder="0"
                  />
                </div>
              </div>
              {brandRowsTotal > 0 && (
                <div className="col-span-2 text-right text-sm font-semibold text-gray-800">
                  Subtotal: ₹{brandRowsTotal.toLocaleString("en-IN")}
                </div>
              )}
            </div>

            {/* Remarks */}
            <div>
              <Label htmlFor="purchase-remarks">Remarks</Label>
              <Input
                id="purchase-remarks"
                value={newPurchase.remarks || ""}
                onChange={(e) => setNewPurchase({ ...newPurchase, remarks: e.target.value })}
              />
            </div>
          </div>

          {purchaseDialogError && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {purchaseDialogError}
            </p>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPurchaseForm(false)}>Cancel</Button>
            <Button className="bg-green-600 hover:bg-green-700" onClick={handlePurchaseSubmit} disabled={loading}>
              {loading && <Clock className="mr-2 h-4 w-4 animate-spin" />}
              Add Purchase
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Issue Stock Dialog */}
      <Dialog open={showIssueForm} onOpenChange={setShowIssueForm}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Issue Stock</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-4">
            {/* Item */}
            <div>
              <Label>Item</Label>
              <div className="mt-1 py-2 text-sm font-medium text-gray-700">
                {formatItemDisplayName(selectedItem)} (Available: {selectedItem?.quantity_available}{" "}
                {selectedItem?.unit_of_measurement || ""})
              </div>
            </div>

            {/* Quantity */}
            <div>
              <Label htmlFor="issue-quantity">Quantity *</Label>
              <Input
                id="issue-quantity"
                type="number"
                step="any"
                min={1}
                max={selectedItem?.quantity_available || 0}
                value={newIssue.quantity}
                onChange={(e) => setNewIssue({ ...newIssue, quantity: Number.parseFloat(e.target.value) || 0 })}
                onFocus={(e) => e.target.select()}
                className="mt-1"
              />
            </div>

            {/* Issued To (internal note) */}
            <div>
              <Label htmlFor="issue-to">Issued To</Label>
              <Input
                id="issue-to"
                value={newIssue.issued_to}
                onChange={(e) => setNewIssue({ ...newIssue, issued_to: e.target.value })}
                placeholder="Enter person name (optional)"
                className="mt-1"
              />
            </div>

            {/* Party Name with search + create */}
            <div className="border-t pt-4">
              <Label>Party Name</Label>
              <p className="text-xs text-gray-400 mb-1.5">Select existing party or type to create new</p>
              <div className="relative" ref={partyRef}>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search or enter party name..."
                    className="pl-9"
                    value={selectedParty ? selectedParty.name : partySearch}
                    onChange={(e) => {
                      setPartySearch(e.target.value)
                      setSelectedParty(null)
                      setShowPartyDrop(true)
                    }}
                    onFocus={() => setShowPartyDrop(true)}
                    onBlur={() => setTimeout(() => setShowPartyDrop(false), 150)}
                  />
                  {selectedParty && (
                    <button
                      onClick={() => { setSelectedParty(null); setPartySearch(""); setPaymentMode("") }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs"
                    >✕</button>
                  )}
                </div>

                {showPartyDrop && !selectedParty && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {filteredParties.length > 0 && filteredParties.map(c => (
                      <button
                        key={c._id}
                        className="w-full text-left px-4 py-2.5 hover:bg-gray-50 flex items-center gap-3 border-b border-gray-50 last:border-0"
                        onMouseDown={() => {
                          setSelectedParty(c)
                          setPartySearch(c.name)
                          setShowPartyDrop(false)
                        }}
                      >
                        <div className="bg-blue-100 rounded-full p-1.5">
                          <User className="w-3 h-3 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{c.name}</p>
                          {c.phone && <p className="text-xs text-gray-400">{c.phone}</p>}
                        </div>
                      </button>
                    ))}
                    {partySearch.trim() && !exactMatch && (
                      <button
                        className="w-full text-left px-4 py-2.5 hover:bg-blue-50 flex items-center gap-3 text-blue-600 border-t border-gray-100"
                        onMouseDown={handleCreateAndSelectParty}
                        disabled={creatingParty}
                      >
                        <div className="bg-blue-600 rounded-full p-1.5">
                          <Plus className="w-3 h-3 text-white" />
                        </div>
                        <span className="text-sm font-medium">
                          {creatingParty ? "Creating..." : `Create "${partySearch.trim()}" as new party`}
                        </span>
                      </button>
                    )}
                    {filteredParties.length === 0 && !partySearch.trim() && (
                      <div className="px-4 py-3 text-sm text-gray-400 text-center">
                        No parties yet. Type a name to create one.
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Payment Mode — only show if party is selected */}
            {selectedParty && (
              <div>
                <Label>Payment</Label>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {[
                    { value: "yes", label: "Paid", icon: CheckCircle, color: "green" },
                    { value: "partial", label: "Partial", icon: SplitSquareHorizontal, color: "yellow" },
                    { value: "no", label: "Unpaid", icon: AlertCircle, color: "red" },
                  ].map(opt => {
                    const Icon = opt.icon
                    const active = paymentMode === opt.value
                    const colorMap = {
                      green: active ? "bg-green-600 text-white border-green-600" : "border-gray-200 text-gray-600 hover:border-green-400 hover:text-green-600",
                      yellow: active ? "bg-yellow-500 text-white border-yellow-500" : "border-gray-200 text-gray-600 hover:border-yellow-400 hover:text-yellow-600",
                      red: active ? "bg-red-600 text-white border-red-600" : "border-gray-200 text-gray-600 hover:border-red-400 hover:text-red-600",
                    }
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setPaymentMode(opt.value)}
                        className={`flex flex-col items-center gap-1 py-3 rounded-xl border-2 transition-all text-sm font-medium ${colorMap[opt.color]}`}
                      >
                        <Icon className="w-4 h-4" />
                        {opt.label}
                      </button>
                    )
                  })}
                </div>
              </div>
            )}

            {/* Amount fields */}
            {selectedParty && (paymentMode === "yes" || paymentMode === "partial" || paymentMode === "no") && (
              <div className="space-y-3">
                <div>
                  <Label>
                    Total Amount{" "}
                    {paymentMode === "yes"
                      ? <span className="text-gray-400 text-xs font-normal">(optional)</span>
                      : <span className="text-red-500">*</span>}
                  </Label>
                  <div className="relative mt-1">
                    <IndianRupee className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                    <Input
                      type="number"
                      min="0"
                      className="pl-7"
                      placeholder="0.00"
                      value={issueTotal}
                      onChange={(e) => setIssueTotal(e.target.value)}
                      onFocus={(e) => e.target.select()}
                    />
                  </div>
                </div>

                {paymentMode === "partial" && (
                  <div>
                    <Label>Amount Paid Now</Label>
                    <div className="relative mt-1">
                      <IndianRupee className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                      <Input
                        type="number"
                        min="0"
                        className="pl-7"
                        placeholder="0.00"
                        value={issuePaid}
                        onChange={(e) => setIssuePaid(e.target.value)}
                        onFocus={(e) => e.target.select()}
                      />
                    </div>
                  </div>
                )}

                {(paymentMode === "partial" || paymentMode === "no") && (
                  <div>
                    <Label>Due Date</Label>
                    <Input
                      type="date"
                      className="mt-1"
                      value={issueDue}
                      onChange={(e) => setIssueDue(e.target.value)}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Notes */}
            <div>
              <Label htmlFor="issue-notes">Notes</Label>
              <Input
                id="issue-notes"
                value={newIssue.notes}
                onChange={(e) => setNewIssue({ ...newIssue, notes: e.target.value })}
                placeholder="Optional"
                className="mt-1"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowIssueForm(false)}>Cancel</Button>
            <Button className="bg-orange-600 hover:bg-orange-700" onClick={handleIssueSubmit} disabled={loading}>
              {loading && <Clock className="mr-2 h-4 w-4 animate-spin" />}
              Issue Stock
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stock Adjustment Dialog */}
      <Dialog open={showAdjustForm} onOpenChange={setShowAdjustForm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Stock Adjustment</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-4">
            <div>
              <Label>Item</Label>
              <div className="mt-1 py-2 text-sm font-medium text-gray-700">
                {formatItemDisplayName(selectedItem)} (Current: {selectedItem?.quantity_available}{" "}
                {selectedItem?.unit_of_measurement || ""})
              </div>
            </div>
            <div>
              <Label htmlFor="adjust-type">Type</Label>
              <Select
                value={newAdjustment.type || "none"}
                onValueChange={(value) => setNewAdjustment({ ...newAdjustment, type: value === "none" ? "" : value })}
              >
                <SelectTrigger id="adjust-type" className="mt-1">
                  <SelectValue placeholder="Select type (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="Error">Error</SelectItem>
                  <SelectItem value="Return">Return</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="adjust-new-quantity">New Quantity *</Label>
              <Input
                id="adjust-new-quantity"
                type="number"
                step="any"
                min={0}
                value={newAdjustment.new_quantity}
                onChange={(e) =>
                  setNewAdjustment({ ...newAdjustment, new_quantity: Number.parseFloat(e.target.value) || 0 })
                }
                onFocus={(e) => e.target.select()}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="adjust-reason">Reason</Label>
              <Input
                id="adjust-reason"
                value={newAdjustment.reason}
                onChange={(e) => setNewAdjustment({ ...newAdjustment, reason: e.target.value })}
                placeholder="Optional"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="adjust-notes">Notes</Label>
              <Input
                id="adjust-notes"
                value={newAdjustment.notes}
                onChange={(e) => setNewAdjustment({ ...newAdjustment, notes: e.target.value })}
                placeholder="Optional"
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdjustForm(false)}>
              Cancel
            </Button>
            <Button className="bg-purple-600 hover:bg-purple-700" onClick={handleAdjustStock} disabled={loading}>
              {loading && <Clock className="mr-2 h-4 w-4 animate-spin" />}
              Apply Adjustment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Release Hold Dialog */}
      <Dialog open={showUnholdForm} onOpenChange={setShowUnholdForm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Release Hold Stock</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-4">
            <div>
              <Label>Item</Label>
              <div className="mt-1 py-2 text-sm font-medium text-gray-700">
                {formatItemDisplayName(selectedItem)} (On Hold: {selectedItem?.on_hold_quantity || 0}{" "}
                {selectedItem?.unit_of_measurement || ""})
              </div>
            </div>
            <div>
              <Label htmlFor="unhold-quantity">Quantity to release *</Label>
              <Input
                id="unhold-quantity"
                type="number"
                step="any"
                min={0}
                max={selectedItem?.on_hold_quantity || 0}
                value={unholdQuantity}
                onChange={(e) => setUnholdQuantity(Number.parseFloat(e.target.value) || 0)}
                onFocus={(e) => e.target.select()}
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUnholdForm(false)}>
              Cancel
            </Button>
            <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={handleUnholdStock} disabled={loading}>
              {loading && <Clock className="mr-2 h-4 w-4 animate-spin" />}
              Release Hold
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hold Dialog */}
      <Dialog open={showHoldForm} onOpenChange={setShowHoldForm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Place Stock On Hold</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-4">
            <div>
              <Label>Item</Label>
              <div className="mt-1 py-2 text-sm font-medium text-gray-700">
                {formatItemDisplayName(selectedItem)} (Available: {selectedItem?.quantity_available}{" "}
                {selectedItem?.unit_of_measurement || ""})
              </div>
            </div>
            <div>
              <Label htmlFor="hold-quantity">Quantity to hold *</Label>
              <Input
                id="hold-quantity"
                type="number"
                step="any"
                min={0}
                max={selectedItem?.quantity_available || 0}
                value={holdQuantity}
                onChange={(e) => setHoldQuantity(Number.parseFloat(e.target.value) || 0)}
                onFocus={(e) => e.target.select()}
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHoldForm(false)}>
              Cancel
            </Button>
            <Button className="bg-amber-600 hover:bg-amber-700" onClick={handleHoldStock} disabled={loading}>
              {loading && <Clock className="mr-2 h-4 w-4 animate-spin" />}
              Place On Hold
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

